import { useEffect, useRef } from 'react';
import { OVERLAY_PRIORITY, useOverlaySlot } from '@context/overlayGate';
import { usePaulina } from './PaulinaProvider';
import type { Nudge } from './usePaulinaChat';

/**
 * usePaulinaTipGate — rutea el TIP PROACTIVO de Paulina por el OverlayGate (PRJ-009), en vez de que
 * cada superficie decida su visibilidad con un `fixed` de z-index alto por su cuenta.
 *
 * Reclama el slot `PAULINA_TIP_SLOT_ID` con la prioridad MÁS BAJA (`paulinaProactiveTip`): cede ante
 * el welcome coachmark (el saludo va primero), ante los OS-nags (instalar/notificaciones) y los
 * anuncios, y ante un pedido entrante (el gate hace `yielding` global) — todo por la vía canónica,
 * respetando "un nag por sesión" junto con welcome/PWA/notif. No consume el slot de OS-nag (no es un
 * OS-nag), así que nunca bloquea esos prompts.
 *
 * La bubble de desktop (PaulinaNudgeBubble, en AgentChatWidget) y el teaser de móvil
 * (PaulinaNudgeTeaserMobile, en MobileTopBarV4) son EXCLUYENTES por viewport (solo una monta a la
 * vez), así que comparten el mismo slot sin colisión.
 *
 * Cuando el gate REALMENTE concede la superficie (pintado), marca el tip como MOSTRADO una sola vez
 * (marcarNudgeMostrado → one-and-done + cap para descubrimiento, cooldown durable para accionables).
 * Marcar al pintar (no al seleccionar el nudge) evita "quemar" el one-and-done si un pedido difiere la
 * superficie — mismo criterio que el welcome coachmark.
 */

// Id único del slot del tip proactivo en el OverlayGate.
export const PAULINA_TIP_SLOT_ID = 'paulina-proactive-tip';

export const usePaulinaTipGate = (nudge: Nudge | null, eligible: boolean): boolean => {
  const paulina = usePaulina();
  const granted = useOverlaySlot(PAULINA_TIP_SLOT_ID, OVERLAY_PRIORITY.paulinaProactiveTip, eligible && Boolean(nudge));

  // Marca "mostrado" (durable) una sola vez por nudge concedido. El ref evita que el doble-montaje de
  // StrictMode o un re-render lo cuenten de más; la marca en sí ya es idempotente por id.
  const shownRef = useRef<string | null>(null);
  useEffect(() => {
    if (!granted || !nudge) return;
    if (shownRef.current === nudge.id) return;
    shownRef.current = nudge.id;
    paulina?.marcarNudgeMostrado(nudge);
  }, [granted, nudge, paulina]);

  return granted && Boolean(nudge);
};
